import { forwardRef, type ReactNode } from 'react';
import { Badge as MantineBadge, type BadgeProps as MantineBadgeProps } from '@mantine/core';
import { alpha, inks } from '../../theme/tokens';

export type BadgeVariant = 'success' | 'info' | 'warn' | 'danger' | 'neutral';
export type BadgeSize = 'xs' | 'sm' | 'md';

export interface ForgeBadgeProps extends Omit<MantineBadgeProps, 'color' | 'size' | 'variant'> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children?: ReactNode;
}

const VARIANT_TO_COLOR: Record<BadgeVariant, { fg: string; bg: string; border: string }> = {
  // fg uses the accent INK (see theme/tokens.ts): the bright accent on its own
  // 10% wash is ~3.2:1 in light mode — below AA, and worse at Badge's uppercase
  // ~11px. The ink aliases back to the bright accent in dark mode.
  success: { fg: inks.neon, bg: alpha('neon', 0.1), border: alpha('neon', 0.4) },
  info: { fg: inks.hudCyan, bg: alpha('hudCyan', 0.1), border: alpha('hudCyan', 0.4) },
  warn: { fg: inks.hudAmber, bg: alpha('hudAmber', 0.1), border: alpha('hudAmber', 0.4) },
  danger: { fg: inks.hudRed, bg: alpha('hudRed', 0.1), border: alpha('hudRed', 0.4) },
  neutral: { fg: 'rgb(var(--c-txt-2))', bg: 'rgb(var(--c-panel))', border: 'var(--c-bd)' },
};

/**
 * Badge — small status pill used inline next to titles, list rows, and KPIs.
 * `Pill` is the dismissible / interactive cousin; reach for `Badge` when the
 * label is read-only.
 */
export const Badge = forwardRef<HTMLDivElement, ForgeBadgeProps>(function Badge(
  { variant = 'neutral', size = 'sm', children, style, ...rest },
  ref,
) {
  const palette = VARIANT_TO_COLOR[variant];

  return (
    <MantineBadge
      ref={ref}
      size={size}
      radius="sm"
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...style,
      }}
      {...rest}
    >
      {children}
    </MantineBadge>
  );
});
